<template>
  <v-container class="import-cmm-page" fluid max-width="760">
    <header class="mb-6">
      <h1 class="text-primary weight-600 mb-1">Import CMM Submission</h1>
      <p class="text-body-2 text-medium-emphasis mb-0">
        Enter the CoverMyMeds key and patient details. We fetch the request from CoverMyMeds.
      </p>
    </header>

    <v-form ref="formRef" @submit.prevent="handleSubmit">
      <v-card>
        <section class="import-section pa-4 pa-sm-6">
          <h2 class="import-section__title">Submission</h2>
          <v-row>
            <v-col cols="12">
              <!-- autocomplete/name chosen to avoid the browser's one-time-code autofill dropping
                     an emailed MFA code into this field (LAM-4598). -->
              <v-text-field
                v-model="submissionKey"
                label="CMM Submission Key"
                placeholder="e.g. BXNQ2K3P"
                autocomplete="off"
                name="cmm-submission-key"
                :rules="[rules.required, rules.cmmKey]"
                hide-details="auto"
                class="lh--force-white-background" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="patientLastName"
                label="Patient Last Name"
                placeholder="Enter patient last name"
                :rules="[rules.required]"
                hide-details="auto"
                class="lh--force-white-background" />
            </v-col>
            <v-col cols="12" sm="6">
              <TextDateInput
                v-model="patientDobDate"
                label="Patient Date of Birth"
                :rules="[rules.required]"
                hide-details="auto"
                class="lh--force-white-background" />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="extraContextNotes"
                label="Additional Context (optional)"
                placeholder="e.g. medical history, vaccinations, payor requirements, requests for expedited review, etc."
                rows="3"
                :maxlength="CONTEXT_MAX_LENGTH"
                :counter="extraContextNotes.length > CONTEXT_COUNTER_FROM ? CONTEXT_MAX_LENGTH : undefined"
                hide-details="auto"
                class="lh--force-white-background" />
            </v-col>
          </v-row>
        </section>

        <v-divider />

        <section class="import-section advanced pa-4 pa-sm-6">
          <button
            type="button"
            class="advanced__toggle"
            :aria-expanded="showAdvanced"
            @click="showAdvanced = !showAdvanced">
            <span class="advanced__heading">
              <span class="import-section__title mb-1">Additional details</span>
              <span class="text-body-2 text-medium-emphasis">
                Optional. Values CoverMyMeds may need that do not always import.
              </span>
            </span>
            <v-icon size="22" class="advanced__chevron" :class="{ 'advanced__chevron--open': showAdvanced }">
              mdi-chevron-down
            </v-icon>
          </button>

          <v-expand-transition @after-enter="onAdvancedOpened">
            <div v-show="showAdvanced" class="advanced__body">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-combobox
                    v-model="packageSizeUnit"
                    :items="packageSizeUnits"
                    label="Package size unit"
                    placeholder="e.g. Gram, Each, mL"
                    :rules="packageSizeUnits.length ? [(v) => valueInItemsRule(v, packageSizeUnits)] : []"
                    hide-details="auto"
                    class="lh--force-white-background">
                    <template #append-inner>
                      <v-tooltip location="top" max-width="320" open-delay="100">
                        <template #activator="{ props }">
                          <v-icon v-bind="props" size="15" class="advanced__help"> mdi-help-circle-outline </v-icon>
                        </template>
                        <span>
                          CoverMyMeds requires a package size unit (dosage form) to submit. It is often not carried over
                          from the pharmacy system or the prescription, so setting it here keeps the submission from
                          stalling.
                        </span>
                      </v-tooltip>
                    </template>
                  </v-combobox>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="patientPhone"
                    type="tel"
                    label="Patient phone number"
                    placeholder="e.g. (555) 123-4567"
                    :rules="[usPhoneRule]"
                    hide-details="auto"
                    class="lh--force-white-background">
                    <template #append-inner>
                      <v-tooltip location="top" max-width="320" open-delay="100">
                        <template #activator="{ props }">
                          <v-icon v-bind="props" size="15" class="advanced__help"> mdi-help-circle-outline </v-icon>
                        </template>
                        <span>
                          Plans frequently require a patient phone number. Adding it here avoids a follow-up when
                          CoverMyMeds did not capture one.
                        </span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="daw"
                    :items="['Yes', 'No']"
                    label="DAW"
                    clearable
                    placeholder="Default (No)"
                    hide-details="auto"
                    class="lh--force-white-background">
                    <template #append-inner>
                      <v-tooltip location="top" max-width="320" open-delay="100">
                        <template #activator="{ props }">
                          <v-icon v-bind="props" size="15" class="advanced__help"> mdi-help-circle-outline </v-icon>
                        </template>
                        <span>
                          Set to Yes when the prescriber requires the brand be dispensed as written. Leave blank to use
                          the default (No).
                        </span>
                      </v-tooltip>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="prescriptionNumber"
                    label="Rx number"
                    placeholder="e.g. 1234567"
                    hide-details="auto"
                    class="lh--force-white-background">
                    <template #append-inner>
                      <v-tooltip location="top" max-width="320" open-delay="100">
                        <template #activator="{ props }">
                          <v-icon v-bind="props" size="15" class="advanced__help"> mdi-help-circle-outline </v-icon>
                        </template>
                        <span>
                          The pharmacy's prescription (Rx) number, if available. Allows looking up this order by Rx
                          number later.
                        </span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="expeditedReview"
                    :items="expeditedOptions"
                    label="Expedited review"
                    clearable
                    placeholder="Use default"
                    hide-details="auto"
                    class="lh--force-white-background">
                    <template #append-inner>
                      <v-tooltip location="top" max-width="320" open-delay="100">
                        <template #activator="{ props }">
                          <v-icon v-bind="props" size="15" class="advanced__help"> mdi-help-circle-outline </v-icon>
                        </template>
                        <span>
                          Request an urgent (within 24h) review for this order. Leave blank to use your account default.
                        </span>
                      </v-tooltip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </section>

        <v-divider />

        <section class="import-section pa-4 pa-sm-6">
          <h2 class="import-section__title mb-1">Documents</h2>
          <ChartPagePickerDialog
            v-if="pickerProps"
            :model-value="true"
            :download-url="pickerProps.downloadUrl"
            :per-page-bytes="pickerProps.perPageBytes"
            :budget-bytes="pickerProps.budgetBytes"
            :allow-over-budget="pickerProps.allowOverBudget"
            @update:model-value="!$event && closePicker()"
            @confirm="confirmPicker" />
          <DocumentUploadList
            ref="documentListRef"
            :docs="supplementalDocs"
            :disabled="isSubmitting || !!importedOrderKey"
            :budget-caption="budgetCaption()"
            :can-choose-pages="canChoosePages"
            @add-files="addFiles"
            @remove="removeDoc"
            @update-send-to-payer="setSendToPayer"
            @update-label="(index, label) => (supplementalDocs[index].label = label)"
            @choose-pages="openPicker" />
        </section>

        <v-divider />

        <section class="import-section pa-4 pa-sm-6">
          <h2 class="import-section__title">Prescriber</h2>
          <v-row>
            <v-col cols="12">
              <ProviderDefaultCommunicationMethod
                ref="providerDefaultCommunicationMethodRef"
                v-model:enabled="forwardResponsesToProviderByEmail"
                v-model:email="providerEmail"
                :show-errors="importFormSubmitted" />
            </v-col>
            <v-col v-if="doaAttestationEnabled" cols="12" sm="6">
              <v-select
                id="doa-attestation"
                v-model="prescriberDoaAttested"
                :items="doaOptions"
                label="DOA with prescriber"
                clearable
                placeholder="Default (No)"
                hide-details="auto"
                class="lh--force-white-background">
                <template #append-inner>
                  <v-tooltip location="top" max-width="320" open-delay="100">
                    <template #activator="{ props }">
                      <v-icon v-bind="props" size="15" class="advanced__help"> mdi-help-circle-outline </v-icon>
                    </template>
                    <span>
                      Set to Yes if there is a delegation of authority (DOA) with this prescriber. Leave blank for No.
                    </span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </section>

        <v-divider />

        <section class="import-section pa-4 pa-sm-6">
          <div class="d-flex justify-end">
            <v-btn
              color="primary"
              size="large"
              rounded="lg"
              type="submit"
              :block="xs"
              :loading="isSubmitting"
              :disabled="isSubmitting || !!importedOrderKey">
              Import Submission
            </v-btn>
          </div>
          <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mt-4">
            <div v-for="error in errors" :key="error">{{ error }}</div>
          </v-alert>

          <v-alert v-if="statusMessage" type="info" variant="tonal" class="mt-4">
            {{ statusMessage }}
          </v-alert>

          <div v-if="importedOrderKey && !isDuplicate" class="import-routing mt-4">
            <IntakeTagBar
              :tags="importedTags"
              :tag-defs="paAvailableTags"
              :tag-due-dates="importedTagDueDates"
              entity-type="submission"
              :entity-id="importedOrderKey"
              @update:tags="(t) => (importedTags = t)"
              @update:tag-due-dates="(d) => (importedTagDueDates = d)" />
            <AssigneeBar
              :assignees="importedAssignees"
              entity-type="submission"
              :entity-id="importedOrderKey"
              @update:assignees="(a) => (importedAssignees = a)" />
            <GroupBar
              :assigned-groups="importedAssignedGroups"
              entity-type="submission"
              :entity-id="importedOrderKey"
              @update:assigned-groups="(g) => (importedAssignedGroups = g)" />
          </div>

          <div v-if="importedOrderKey" class="d-flex ga-3 mt-4">
            <v-btn color="primary" variant="outlined" rounded="lg" @click="viewOrder">
              {{ isDuplicate ? "View Existing Submission" : "View Order" }}
            </v-btn>
            <v-btn color="primary" variant="tonal" rounded="lg" @click="resetForm">
              {{ isDuplicate ? "Try a Different Key" : "Import Another" }}
            </v-btn>
          </div>
        </section>
      </v-card>
    </v-form>

    <!-- No Documents Warning Dialog -->
    <v-dialog v-model="showNoDocsWarning" max-width="480" persistent>
      <v-card class="pa-2">
        <v-card-text class="text-center pt-6 pb-2">
          <v-icon size="48" color="warning" class="mb-4">mdi-file-alert-outline</v-icon>
          <h3 class="text-primary weight-600 mb-3">No Documents Attached</h3>
          <p class="text-body-1 text-medium-emphasis mb-2">
            {{ noDocsReason }}
            The order will not be processed until the required documentation has been provided.
          </p>
          <p class="text-body-2 text-medium-emphasis">
            Would you like to go back and upload documents, or proceed without them?
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-2">
          <v-spacer />
          <v-btn variant="outlined" color="primary" @click="showNoDocsWarning = false">Go Back</v-btn>
          <v-btn variant="flat" color="warning" :loading="isSubmitting" @click="confirmImportWithoutDocs">
            Import Anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { orderInfoService } from "@/services/orderInfoService";
import { tryParseApiErrors } from "@/utils";
import { localDateToIso } from "@/utils/dateUtils";
import { valueInItemsRule, usPhoneRule } from "@/utils/form";
import { CMM_KEY_REGEX } from "@/utils/validators";
import TextDateInput from "@/components/TextDateInput.vue";
import ProviderDefaultCommunicationMethod from "@/components/ProviderDefaultCommunicationMethod.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useDemoCmmImport } from "@/demo/useDemoCmmImport";
import { MAX_AI_ONLY_DOCUMENTS, OVER_BUDGET_MESSAGE, useChartUpload } from "@/composables/useChartUpload";
import ChartPagePickerDialog from "@/components/ChartPagePickerDialog.vue";
import DocumentUploadList from "@/components/DocumentUploadList.vue";

const route = useRoute();
const router = useRouter();
const requirementsStore = useRequirementsStore();
const { xs } = useDisplay();
const formRef = ref(null);

const submissionKey = ref("");
const patientLastName = ref("");
const patientDobDate = ref(null);
const extraContextNotes = ref("");
const patientPhone = ref("");
const packageSizeUnit = ref(null);
const daw = ref(null);
const prescriptionNumber = ref("");
const expeditedReview = ref(null);
const showAdvanced = ref(true);

// Package size unit options are the canonical DRUG_QUANTITY_UNITS list, served via client config.
const packageSizeUnits = computed(() => requirementsStore.clientConfig?.intake?.packageSizeUnits || []);
const paAvailableTags = computed(() => requirementsStore.clientConfig?.priorAuth?.availableTags || []);
// Tri-state: Yes/No override, or blank to fall back to the client's default_expedited_review.
const expeditedOptions = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const CONTEXT_MAX_LENGTH = 3500;
const CONTEXT_COUNTER_FROM = 3000;
const {
  docs: supplementalDocs,
  addFiles,
  setSendToPayer,
  removeDoc,
  resetDocs,
  hasPayerDocs,
  isAnyUploadInProgress,
  isOverBudget,
  budgetCaption,
  canChoosePages,
  pickerProps,
  openPicker,
  closePicker,
  confirmPicker,
} = useChartUpload();
const documentListRef = ref(null);
const isSubmitting = ref(false);
const errors = ref([]);
const statusMessage = ref(null);
const importedOrderKey = ref(null);
const importedAssignees = ref([]);
const importedAssignedGroups = ref([]);
const importedTags = ref([]);
const importedTagDueDates = ref([]);
const isDuplicate = ref(false);
const isReimport = ref(false);
const showNoDocsWarning = ref(false);
const noDocsReason = computed(() => {
  if (!supplementalDocs.value.length) return "This submission does not include any supporting documents.";
  if (supplementalDocs.value.some((doc) => doc.sendToPayer)) {
    return "A file marked Send to payer did not finish preparing, so no supporting documents go to the payer.";
  }
  return "No file is marked Send to payer, so no supporting documents go to the payer.";
});
const isPasting = () => documentListRef.value?.pasting === true;
const forwardResponsesToProviderByEmail = ref(false);
// Smart Submit (LAM-5919): the DOA attestation can be given at import and changed on the review page.
const doaAttestationEnabled = computed(
  () =>
    requirementsStore.clientConfig?.priorAuth?.delegateAccountSubmitRequired === true &&
    requirementsStore.clientConfig?.priorAuth?.smartSubmitEnabled === true
);
// Yes/No like the other Additional details selects; blank means No.
const prescriberDoaAttested = ref(null);
const doaOptions = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const providerEmail = ref(null);
const providerDefaultCommunicationMethodRef = ref(null);
const importFormSubmitted = ref(false);

onMounted(async () => {
  const prefillKey = route.query.key;
  // Don't clobber a key the user already typed before this component mounted.
  if (typeof prefillKey === "string" && prefillKey.trim() && !submissionKey.value) {
    submissionKey.value = prefillKey.trim().toUpperCase();
  }
  // Ensure package size unit options are available even if the sidebar hasn't loaded client config yet.
  // Non-fatal if it fails — the combobox still accepts free text and the backend validates.
  if (!requirementsStore.clientConfig) {
    try {
      await requirementsStore.refreshCurrentUserClientConfig();
    } catch (e) {
      // ignore — leave the dropdown empty rather than block the import form
    }
  }
});

const rules = {
  required: (v) => !!v || "This field is required",
  cmmKey: (v) => {
    if (!v) return true;
    const cleaned = v
      .replace(/^key\s*:\s*/i, "")
      .trim()
      .toUpperCase();
    return CMM_KEY_REGEX.test(cleaned) || "Enter a valid CMM key (6\u20138 letters/digits, e.g. BXNQ2K3P).";
  },
};

const patientDobIso = computed(() => localDateToIso(patientDobDate.value));

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  errors.value = [];
  importFormSubmitted.value = true;

  const { valid } = await formRef.value.validate();
  if (!valid || !providerDefaultCommunicationMethodRef.value?.validate()) {
    await scrollToFirstInvalidField();
    return;
  }

  if (isAnyUploadInProgress() || isPasting()) {
    errors.value = ["Please wait for file uploads to complete before importing."];
    return;
  }

  if (isOverBudget()) {
    errors.value = [OVER_BUDGET_MESSAGE];
    return;
  }

  if ((documentListRef.value?.aiOnlyCount ?? 0) > MAX_AI_ONLY_DOCUMENTS) {
    errors.value = [`At most ${MAX_AI_ONLY_DOCUMENTS} files can be for our autocomplete tool only.`];
    return;
  }

  if (!hasPayerDocs()) {
    showNoDocsWarning.value = true;
    return;
  }

  await performSubmit();
};

const confirmImportWithoutDocs = async () => {
  if (isAnyUploadInProgress() || isPasting()) {
    showNoDocsWarning.value = false;
    errors.value = ["Please wait for file uploads to complete before importing."];
    return;
  }
  showNoDocsWarning.value = false;
  importFormSubmitted.value = true;
  if (!providerDefaultCommunicationMethodRef.value?.validate()) {
    await scrollToFirstInvalidField();
    return;
  }
  await performSubmit();
};

let fieldToScrollOnAdvancedOpen = null;

const centerInView = (el) => el.scrollIntoView({ behavior: "smooth", block: "center" });

/** Center the first invalid field (label included) in view; inside a collapsed Additional details, once it opens. */
const scrollToFirstInvalidField = async () => {
  await nextTick();
  const firstInvalid = formRef.value?.$el?.querySelector(".v-input--error");
  fieldToScrollOnAdvancedOpen = null;
  if (!firstInvalid) return;
  if (!showAdvanced.value && firstInvalid.closest(".advanced__body")) {
    fieldToScrollOnAdvancedOpen = firstInvalid;
    showAdvanced.value = true;
    return;
  }
  centerInView(firstInvalid);
};

const onAdvancedOpened = () => {
  if (fieldToScrollOnAdvancedOpen) centerInView(fieldToScrollOnAdvancedOpen);
  fieldToScrollOnAdvancedOpen = null;
};

const performSubmit = async () => {
  submissionKey.value = submissionKey.value
    .replace(/^key\s*:\s*/i, "")
    .trim()
    .toUpperCase();
  const extraContext = extraContextNotes.value.trim();
  errors.value = [];
  statusMessage.value = null;
  isSubmitting.value = true;

  try {
    const uploadedDocs = supplementalDocs.value.filter((doc) => doc.links.length > 0);
    const toChartEntry = (doc) => ({ label: doc.label || doc.file?.name || "Document", links: doc.links, context: "" });
    const payload = {
      submission_key: submissionKey.value,
      patient_last_name: patientLastName.value,
      patient_dob: patientDobIso.value,
      patient_chart: uploadedDocs.filter((doc) => doc.sendToPayer).map(toChartEntry),
      ai_reference_documents: uploadedDocs.filter((doc) => !doc.sendToPayer).map(toChartEntry),
    };
    if (extraContext) {
      payload.extra_structured_information = { additional_context: extraContext };
    }
    const phone = patientPhone.value?.trim();
    if (phone) payload.patient_phone = phone;
    const unit = typeof packageSizeUnit.value === "string" ? packageSizeUnit.value.trim() : packageSizeUnit.value;
    if (unit) payload.package_size_unit = unit;
    if (daw.value) payload.daw = daw.value;
    const rxNum = prescriptionNumber.value?.trim();
    if (rxNum) payload.prescription_number = rxNum;
    // Send only when explicitly set (Yes/No); blank leaves it to the client default. Guard on null,
    // not falsiness, so an explicit "No" (false) is preserved.
    if (expeditedReview.value !== null && expeditedReview.value !== undefined) {
      payload.expedited_review_within24h = expeditedReview.value;
    }
    payload.forward_responses_to_provider_by_email = forwardResponsesToProviderByEmail.value;
    if (doaAttestationEnabled.value) payload.prescriber_doa_attested = prescriberDoaAttested.value === true;
    if (forwardResponsesToProviderByEmail.value) {
      payload.provider_email = providerEmail.value?.trim();
    }
    const response = await orderInfoService.importCMMSubmission(payload);
    const orderKey = response?.orderKey;
    if (!orderKey) {
      errors.value = ["Submission was created but no order key was returned. Please check All Submissions."];
      return;
    }
    importedOrderKey.value = orderKey;
    importedAssignees.value = response?.assignees || [];
    importedAssignedGroups.value = response?.assignedGroups || [];
    importedTags.value = response?.statusTags || [];
    importedTagDueDates.value = response?.tagDueDates || [];
    isReimport.value = !!response?.wasReimport;
    statusMessage.value = isReimport.value
      ? "Patient information updated. Re-running the import with the corrected details."
      : "Import started. We're fetching this submission from CoverMyMeds.";
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    if (errors.value.length === 0) {
      errors.value = ["An unexpected error occurred. Please try again."];
    }
    // Error bodies skip camelization and DRF quotes their numbers.
    const oversizedDocuments = error?.response?.data?.documents;
    if (Array.isArray(oversizedDocuments)) {
      errors.value.push(
        ...oversizedDocuments.map((doc) => `${doc.label}: ${(Number(doc.size) / (1024 * 1024)).toFixed(1)} MB`)
      );
    }
    // 409 duplicate-key: offer a link to the pre-existing submission
    const existingKey = error?.response?.data?.existing_order_key;
    if (error?.response?.status === 409 && existingKey) {
      importedOrderKey.value = existingKey;
      isDuplicate.value = true;
    }
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  submissionKey.value = "";
  patientLastName.value = "";
  patientDobDate.value = null;
  extraContextNotes.value = "";
  patientPhone.value = "";
  packageSizeUnit.value = null;
  daw.value = null;
  prescriptionNumber.value = "";
  expeditedReview.value = null;
  showAdvanced.value = true;
  resetDocs();
  errors.value = [];
  statusMessage.value = null;
  importedOrderKey.value = null;
  importedAssignees.value = [];
  importedAssignedGroups.value = [];
  importedTags.value = [];
  importedTagDueDates.value = [];
  isDuplicate.value = false;
  isReimport.value = false;
  showNoDocsWarning.value = false;
  forwardResponsesToProviderByEmail.value = false;
  prescriberDoaAttested.value = null;
  providerEmail.value = null;
  importFormSubmitted.value = false;
  formRef.value?.resetValidation();
};

const viewOrder = () => {
  router.push({ name: "check-my-coverage", params: { id: importedOrderKey.value } });
};

// Demo mode only: prefill the form and auto-advance into the live PA flow. No-op in production.
useDemoCmmImport({
  submissionKey,
  patientLastName,
  patientDobDate,
  extraContextNotes,
  supplementalDocs,
  importedOrderKey,
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables";

.import-section {
  &__title {
    display: block;
    margin: 0 0 12px;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5;
    color: rgb(var(--v-theme-primary));
  }
}

.advanced {
  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
  }

  &__heading {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__chevron {
    color: $lh_color_blue;
    transition: transform $lh_transition_fast;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding-top: 0.5rem;
  }

  &__help {
    margin-left: 4px;
    color: $lh_color_blue_gray;
    cursor: help;
  }
}
</style>
